import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { useEffect, useRef, useState } from 'react';
import type { Env } from '@fleet-cert-console/shared';
import { vpnFilePushPresign, vpnFilePushStart, vpnFilePushStatus } from '../api/client.js';

const MAX_FILE_BYTES = 200 * 1024 * 1024; // keep in sync with the BFF's file-push.ts

type Phase = 'pick' | 'uploading' | 'copying' | 'done' | 'error';

export interface VpnFilePushModalProps {
  visible: boolean;
  env: Env;
  gatewayId: string;
  onDismiss: () => void;
}

/** Send a local file to the gateway: presigned S3 upload, then the hub scp's it over the VPN. */
export function VpnFilePushModal({ visible, env, gatewayId, onDismiss }: VpnFilePushModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [destPath, setDestPath] = useState('/tmp/');
  const [phase, setPhase] = useState<Phase>('pick');
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setFile(null);
    setDestPath('/tmp/');
    setPhase('pick');
    setUploadPct(0);
    setError(null);
  };

  const onPick = (f: File | null) => {
    setFile(f);
    if (f) setDestPath(`/tmp/${f.name}`);
  };

  const fail = (message: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPhase('error');
    setError(message);
  };

  const onSend = async () => {
    if (!file) return;
    setError(null);
    try {
      setPhase('uploading');
      const { uploadUrl, s3Key } = await vpnFilePushPresign(env, gatewayId, file.name, file.size);
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.upload.onprogress = (e) => e.lengthComputable && setUploadPct(Math.round((e.loaded / e.total) * 100));
        xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
        xhr.onerror = () => reject(new Error('Upload failed (network)'));
        xhr.send(file);
      });
      setPhase('copying');
      const commandId = await vpnFilePushStart(env, gatewayId, s3Key, destPath.trim());
      pollRef.current = setInterval(() => {
        void vpnFilePushStatus(env, commandId)
          .then((s) => {
            if (s.status === 'success') {
              if (pollRef.current) clearInterval(pollRef.current);
              setPhase('done');
            } else if (s.status === 'failed') {
              fail(s.stderr ?? 'Copy failed on the hub');
            }
          })
          .catch((e) => fail((e as Error).message));
      }, 2000);
    } catch (e) {
      fail((e as Error).message);
    }
  };

  const busy = phase === 'uploading' || phase === 'copying';

  return (
    <Modal
      visible={visible}
      onDismiss={() => { if (!busy) { reset(); onDismiss(); } }}
      header={`Send file to ${gatewayId}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" disabled={busy} onClick={() => { reset(); onDismiss(); }}>
              {phase === 'done' ? 'Close' : 'Cancel'}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!file || file.size > MAX_FILE_BYTES || !destPath.trim() || phase === 'done'}
              onClick={() => void onSend()}
            >
              Send
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error" header="File push failed">{error}</Alert>}
        <FormField label="File">
          <input type="file" disabled={busy} onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
          {file && (
            <Box fontSize="body-s" color={file.size > MAX_FILE_BYTES ? 'text-status-error' : 'text-body-secondary'}>
              {(file.size / 1_000_000).toFixed(1)} MB{file.size > MAX_FILE_BYTES ? ' — exceeds the 200 MB limit' : ''}
            </Box>
          )}
        </FormField>
        <FormField
          label="Destination path on the gateway"
          description="Full file path. /tmp is the only path guaranteed writable across vendors — note it's usually RAM-backed, so a large file occupies memory until moved or the device reboots."
        >
          <Input value={destPath} disabled={busy} onChange={({ detail }) => setDestPath(detail.value)} />
        </FormField>
        {phase === 'uploading' && <ProgressBar value={uploadPct} label="Uploading…" />}
        {phase === 'copying' && <StatusIndicator type="in-progress">Copying to the device over the VPN…</StatusIndicator>}
        {phase === 'done' && <StatusIndicator type="success">Delivered to {destPath}</StatusIndicator>}
      </SpaceBetween>
    </Modal>
  );
}
